import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import MetaControl from 'Components/UI/Meta/MetaControl';
import {
  displaySuccess,
  displayErrorMessage,
} from 'Helpers/FacetNotificationManager';
import Button from 'Components/UI/Button';
import { addSpacesAndCapitalize } from 'Helpers/Formats';
import ConfirmationDialog from 'Components/UI/ConfirmationDialog';

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'center',
  },
  left: {
    flex: '2 0 0',
    marginRight: theme.spacing(1),
  },
  right: {
    flex: '1 0 0',
  },
  label: {
    justifyContent: 'flex-start',
  },
}));

export default function Contact({
  handleUpdate,
  client,
  onAddEmail,
}) {
  const [changeLoginEmailAddressData, setChangeLoginEmailAddressData] = useState(null);
  const deleteChildResource = async (attrName, index) => {
    const resource = client
      .attr(attrName)
      .resources()
      .index(index);

    if (resource.link('self')) {
      try {
        await resource
          .link('self')
          .Delete()
          .then(() => displaySuccess(
            `${addSpacesAndCapitalize(resource.type())} removed`,
          ));
      } catch (err) {
        displayErrorMessage(err, err.errors);
      }
    }

    client
      .attr(attrName)
      .resources()
      .removeItemByIndex(index);
    handleUpdate(client);
  };

  const classes = useStyles();

  return (
    <>
      {client.attr('isPrimary') === 'true' && (
        <>
          <MetaControl
            attrName="addressLine1"
            onChange={value => handleUpdate(client.attr('addressLine1', value))}
            resource={client}
          />
          <MetaControl
            attrName="addressLine2"
            onChange={value => handleUpdate(client.attr('addressLine2', value))}
            resource={client}
          />
          <div className={classes.row}>
            <MetaControl
              className={classes.left}
              attrName="city"
              onChange={value => handleUpdate(client.attr('city', value))}
              resource={client}
            />
            <MetaControl
              className={classes.right}
              attrName="state"
              onChange={value => handleUpdate(client.attr('state', value))}
              resource={client}
            />
          </div>
          <MetaControl
            attrName="postalCode"
            onChange={value => handleUpdate(client.attr('postalCode', value))}
            resource={client}
          />
        </>
      )}
      <MetaControl
        attrName="preferredContact"
        onChange={(value) => {
          client.attr('preferredContact', value);
          handleUpdate(client);
        }}
        resource={client}
      />
      {client.attr('emailAddresses')
                && client
                  .attr('emailAddresses')
                  .resources()
                  .list()
                  .sort((a) => {
                    if (a.attr('isPrimary') === 'true') {
                      return -1;
                    }
                    return 0;
                  })
                  .map((ea) => {
                    const unsortedIndex = client
                      .attr('emailAddresses')
                      .resources()
                      .list()
                      .findIndex(e => e.id() === ea.id());
                    return (
                      <div
                        className={classes.row}
                        key={`email-addresses.${unsortedIndex}`}
                      >
                        <MetaControl
                          className={classes.left}
                          attrName="address"
                          onChange={(value) => {
                            client
                              .attr('emailAddresses')
                              .resources()
                              .index(unsortedIndex)
                              .attr('address', value);
                            handleUpdate(client);
                          }}
                          resource={ea}
                        />
                        <MetaControl
                          className={classes.right}
                          attrName="type"
                          onChange={(value) => {
                            const originalLoginEmailAddress = client.attr('emailAddresses').resources().list().find(e => e.attr('isPrimary') === 'true');
                            if (value === 'login' && ea.id() !== originalLoginEmailAddress?.id()) {
                              setChangeLoginEmailAddressData({
                                originalLoginEmailAddress,
                                newLoginEmailAddress: client
                                  .attr('emailAddresses')
                                  .resources()
                                  .index(unsortedIndex),
                              });
                            } else {
                              client
                                .attr('emailAddresses')
                                .resources()
                                .index(unsortedIndex)
                                .attr('type', value);
                              handleUpdate(client);
                            }
                          }}
                          resource={ea}
                        />
                        {ea.attr('isPrimary') !== 'true' && (
                        <IconButton
                          size="small"
                          aria-label="delete-email-address"
                          onClick={() => deleteChildResource('emailAddresses', unsortedIndex)
                                    }
                        >
                          <DeleteIcon />
                        </IconButton>
                        )}
                      </div>
                    );
                  })}
      <Button
        classes={{
          label: classes.label,
        }}
        color="primaryGray"
        fullWidth
        onClick={onAddEmail}
      >
        <Icon
          className="fa fa-plus-circle"
          color="secondary"
          style={{ fontSize: '16px' }}
        />
        {'Add Email'}
      </Button>
      {changeLoginEmailAddressData && (
        <ConfirmationDialog
          onCancel={() => setChangeLoginEmailAddressData(null)}
          title="Do you want to change your login email?"
          message={`${changeLoginEmailAddressData.newLoginEmailAddress.attr('address')} will become the new login email address`}
          onConfirm={() => {
            changeLoginEmailAddressData.newLoginEmailAddress.attr('type', 'login');
            if (changeLoginEmailAddressData.originalLoginEmailAddress) {
              changeLoginEmailAddressData.originalLoginEmailAddress.attr('type', 'other');
            }
            setChangeLoginEmailAddressData(null);
            handleUpdate(client);
          }}
          showCloseIcon={false}
        />
      )}
    </>
  );
}

Contact.propTypes = {
  handleUpdate: PropTypes.func.isRequired,
  client: PropTypes.object.isRequired,
  onAddEmail: PropTypes.func.isRequired,
};
